import type { ResponseDataWithPagination, ResponseType } from './apiTypes';
import type { Pagination } from './apiTypes';
import type { Nullable } from './common';

export type TaskStatus = 'OPEN' | 'COMPLETED' | 'CLOSED';

export const REMINDER_DAYS = {
  ONE_DAY: 1,
  THREE_DAYS: 3,
  TEN_DAYS: 10,
} as const;

export type ReminderDayType = (typeof REMINDER_DAYS)[keyof typeof REMINDER_DAYS];

export interface TaskUserPreview {
  name: string;
  avatar_url?: string | null;
}

export interface TaskAgentPreview {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  email?: string | null;
  agency_id?: number | null;
  image_relative_url?: string | null;
  profile_picture_url?: string | null;
}

export interface TaskClientPreview {
  id: number;
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  agent_id?: number | null;
  image_relative_url?: string | null;
}

export interface TaskAttachmentMedia {
  uuid: string;
  raw_filename: string;
  file_mime_type: string;
  file_size_bytes?: number | null;
  status?: string | null;
  visibility: 'PUBLIC' | 'INTERNAL' | 'RESTRICTED' | 'SENSITIVE';
  remote_url: string;
}

export interface TaskAttachment {
  uuid: string;
  media_uuid: string;
  media: TaskAttachmentMedia;
  attachable_type?: string;
  attachable_id?: number;
  attachment_role?: string;
  sort_order?: number;
  is_primary?: boolean;
  created_at?: string;
  updated_at?: string;
  // legacy
  id?: number;
  url?: string;
  name?: string;
}

export interface UploadMediaItem {
  uuid: string;
  raw_filename: string;
  file_mime_type: string;
  file_size_bytes: number;
  status: string;
  visibility: string;
  remote_url: string;
}

export interface UploadMediaResponse {
  success: boolean;
  data: UploadMediaItem[];
}

export interface CreateMediaAttachmentRequest {
  media_uuid: string;
  attachable_type: 'TASK';
  attachable_id: number;
  attachment_role: 'TASK_ATTACHMENT';
  sort_order?: number;
  is_primary?: boolean;
}

export type CreateMediaAttachmentResponse = ResponseType<TaskAttachment>;

export interface MediaAttachmentViewResponse {
  success: boolean;
  message?: string;
  data: {
    url: string;
    expires_at: string;
    mode: string;
    ttl_minutes: number;
  };
}

export interface TaskCreatedByPreview {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  email?: string | null;
  image_relative_url?: string | null;
}

export interface TaskReminderPreview {
  uuid: string;
  text?: string | null;
  trigger_at: string;
  status: 'PENDING' | 'SENT' | 'FAILED';
  created_at?: string;
}

export interface ClientTaskData {
  id: number;
  title: string;
  description?: string | null;
  agent_id?: number | null;
  client_id: number;
  deadline: string;
  reminder_days?: Nullable<ReminderDayType>;
  remind_user_at?: string | null;
  status: TaskStatus;
  created_by_id?: number | null;
  updated_by_id?: number | null;
  created_at: string;
  updated_at?: string | null;
  details?: string | null;
  reminder?: TaskReminderPreview | null;
  attachments?: TaskAttachment[];
  agent?: TaskAgentPreview | null;
  client?: TaskClientPreview | null;
  created_by?: TaskCreatedByPreview | null;
  // legacy fields (kept for backwards compat)
  assigned_to?: string;
  files?: TaskAttachment[];
  associated_client?: { name: string; avatar_url?: string | null } | null;
  assigned_agent?: { name: string; avatar_url?: string | null } | null;
}

export interface GetClientTasksResponse extends ResponseType<ClientTaskData[]> {
  pagination?: Pagination;
}

export interface AgentListItem {
  id: number;
  full_name: string;
  avatar_url?: string | null;
  image_relative_url?: string | null;
}

export type GetAgentsListResponse = ResponseDataWithPagination<AgentListItem[]>;

export interface CreateClientTaskRequest {
  agent_id?: number | null;
  client_id: number;
  title: string;
  description?: string;
  deadline?: string;
  reminder_days?: Nullable<ReminderDayType>;
  status: TaskStatus;
}

export type CreateClientTaskResponse = ResponseType<ClientTaskData>;

export interface UpdateClientTaskRequest {
  agent_id?: number | null;
  client_id?: number;
  title?: string;
  description?: string | null;
  status?: TaskStatus;
  deadline?: string;
  reminder_days?: Nullable<ReminderDayType>;
}

export type UpdateClientTaskResponse = ResponseType<ClientTaskData>;

export type DeleteClientTaskResponse = ResponseType<null>;
